import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../sections/Footer';
import { getSanityProducts, ROPA_ITEMS, SHOES_ITEMS, SUP_ITEMS } from '../data/shopProducts';
import type { Product } from '../data/shopProducts';

const SECTIONS = [
  { id: 'apparel', label: 'Apparel' },
  { id: 'shoes', label: 'Shoes' },
  { id: 'supplements', label: 'Supplements' },
] as const;

function ShopHero() {
  return (
    <section className="relative py-20 md:py-28 bg-[var(--vyera-cream)] border-b border-[var(--vyera-warm-grey)]/20">
      <div className="px-[6vw] text-center max-w-2xl mx-auto">
        <p className="vyera-label text-[var(--vyera-text-secondary)] mb-3 tracking-[0.2em]">The collection</p>
        <h1 className="vyera-heading-xl text-[var(--vyera-text-primary)] mb-4">
          Apparel. Shoes. Supplements.
        </h1>
        <p className="vyera-body text-[var(--vyera-text-secondary)]">
          One rhythm. Built for movement and recovery.
        </p>
      </div>
    </section>
  );
}

function ShopCategoryNav({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  return (
    <nav
      className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 flex gap-2 p-1.5 bg-[var(--vyera-cream)]/95 backdrop-blur-md border border-[var(--vyera-warm-grey)]/30 rounded-full shadow-lg"
      aria-label="Shop categories"
    >
      {SECTIONS.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${active === id
            ? 'bg-[var(--vyera-black)] text-[var(--vyera-cream)]'
            : 'text-[var(--vyera-text-primary)] hover:bg-[var(--vyera-black)]/5'
            }`}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

function ProductCard({
  item,
  theme = 'light',
}: {
  item: Product;
  theme?: 'light' | 'dark';
}) {
  const hasPrice = item.price !== '—';
  const isDark = theme === 'dark';

  return (
    <Link to={`/shop/product/${item.id}`} className="block group">
      <article>
        <div className="vyera-img-card vyera-img-card--hover mb-3 relative">
          <img src={item.image} alt={item.name} loading="lazy" />
        </div>
        <p className={`vyera-label text-[10px] mb-1 ${isDark ? 'text-[var(--vyera-burgundy)]' : 'text-[var(--vyera-text-secondary)]'}`}>
          {item.detail || item.category}
        </p>
        <h3 className={`font-medium mb-1 ${isDark ? 'text-[var(--vyera-cream)]' : 'text-[var(--vyera-text-primary)]'}`}>
          {item.name}
        </h3>
        {hasPrice && (
          <p className={`text-sm mb-3 ${isDark ? 'text-[var(--vyera-warm-grey)]' : 'text-[var(--vyera-text-secondary)]'}`}>
            {item.price}
          </p>
        )}
        <span className={`vyera-cta text-[13px] ${isDark ? '!text-[var(--vyera-cream)]' : ''}`}>
          {hasPrice ? 'Select size & add to bag' : 'View details'}
        </span>
      </article>
    </Link>
  );
}

function CategoryBlock({
  id,
  title,
  subtitle,
  items,
  theme = 'light',
}: {
  id: string;
  title: string;
  subtitle: string;
  items: Product[];
  theme?: 'light' | 'dark';
}) {
  const isDark = theme === 'dark';
  return (
    <section
      id={id}
      className={`py-16 md:py-20 ${isDark ? 'bg-[var(--vyera-black)]' : 'bg-[var(--vyera-cream)]'}`}
    >
      <div className="px-[6vw] max-w-[1400px] mx-auto">
        <header className="mb-12">
          <p className={`vyera-label mb-1 tracking-[0.2em] ${isDark ? 'text-[var(--vyera-burgundy)]' : 'text-[var(--vyera-text-secondary)]'}`}>
            {subtitle}
          </p>
          <h2 className={`vyera-heading-lg ${isDark ? 'text-[var(--vyera-cream)]' : 'text-[var(--vyera-text-primary)]'}`}>
            {title}
          </h2>
        </header>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-10 sm:gap-x-6 sm:gap-y-12">
          {items.map((item) => (
            <ProductCard key={item.id} item={item} theme={theme} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Shop() {
  const [activeSection, setActiveSection] = useState('apparel');
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      const data = await getSanityProducts();
      setAllProducts(data);
      setLoading(false);
    };
    loadProducts();
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.id;
          if (id && SECTIONS.some((s) => s.id === id)) setActiveSection(id);
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Filtrar productos por categoría si se cargan desde Sanity
  // Si usamos los arrays estáticos ROPA_ITEMS etc, ignoramos allProducts
  // Pero para que Sanity funcione, debemos reconstruir esos arrays si allProducts tiene datos
  const apparel = allProducts.length > 0 ? allProducts.filter(p => p.category === 'apparel') : ROPA_ITEMS;
  const shoes = allProducts.length > 0 ? allProducts.filter(p => p.category === 'shoes') : SHOES_ITEMS;
  const supplements = allProducts.length > 0 ? allProducts.filter(p => p.category === 'supplements') : SUP_ITEMS;

  return (
    <div className="relative bg-[var(--vyera-cream)] min-h-screen">
      <Navigation />
      <main>
        <ShopHero />
        <ShopCategoryNav active={activeSection} onSelect={scrollToSection} />

        {loading && (
          <div className="py-10 text-center">
            <span className="vyera-label opacity-40 animate-pulse italic">Updating inventory...</span>
          </div>
        )}

        <CategoryBlock
          id="apparel"
          title="Apparel"
          subtitle="Clothing"
          items={apparel}
          theme="light"
        />
        <CategoryBlock
          id="shoes"
          title="Shoes"
          subtitle="Footwear"
          items={shoes}
          theme="light"
        />
        <CategoryBlock
          id="supplements"
          title="POW LEAN"
          subtitle="Vitamin supplement"
          items={supplements}
          theme="dark"
        />

        <Footer />
      </main>
    </div>
  );
}
